import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { METHOD_LABELS, reversePayment } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { listDepartmentMembers } from '@/data/members'
import { closeProject, getProject, listGifts, recordGift, setProjectEndDate } from '@/data/projects'
import type { Gift } from '@/data/projects'
import { formatDay } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { lusakaToday } from '@/lib/periods'
import { percent } from '@/lib/reportMath'
import { amountToNgwee, giftFormSchema, reasonSchema } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const newRef = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `x-${Date.now()}-${Math.random().toString(36).slice(2)}`)

export function ProjectDetailPage({ deptId, projectId }: { deptId: string; projectId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const today = lusakaToday()

  const projectQ = useQuery({ queryKey: ['project', deptId, projectId], queryFn: () => getProject(deptId, projectId) })
  const giftsQ = useQuery({ queryKey: ['gifts', deptId, projectId], queryFn: () => listGifts(deptId, projectId) })
  const membersQ = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })
  const toast = useToast()

  const refresh = async () => {
    for (const k of ['project', 'gifts', 'projects', 'quarter-report', 'position']) await qc.invalidateQueries({ queryKey: [k] })
  }

  // ---------------- record a gift
  const [open, setOpen] = useState(false)
  const [member, setMember] = useState('')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [receivedOn, setReceivedOn] = useState(today)
  const [reference, setReference] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const busy = useRef(false)
  const clientRef = useRef(newRef())
  const giftM = useMutation({ mutationFn: (v: { input: Parameters<typeof recordGift>[2]; ref: string }) => recordGift(deptId, projectId, v.input, v.ref) })

  const openForm = () => {
    setMember(''); setAmount(''); setMethod('cash'); setReceivedOn(today); setReference(''); setErrors({}); setFormError('')
    clientRef.current = newRef()
    setOpen(true)
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setFormError('')
    const parsed = giftFormSchema.safeParse({ memberId: member, amount, method, receivedOn, reference })
    const errs: Record<string, string> = {}
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
    if (parsed.success && parsed.data.receivedOn > today) errs.receivedOn = 'The date cannot be in the future'
    setErrors(errs)
    if (!parsed.success || Object.keys(errs).length > 0) return
    const v = parsed.data
    busy.current = true
    try {
      await giftM.mutateAsync({ ref: clientRef.current, input: { memberId: v.memberId, amountNgwee: amountToNgwee(v.amount) as number, method: v.method, receivedOn: v.receivedOn, reference: v.reference } })
      await refresh()
      setOpen(false)
      toast.success(`${formatKwacha(amountToNgwee(v.amount) as number)} recorded for ${project?.name ?? 'the project'}.`)
    } catch (err) {
      setFormError(errText(err))
    } finally {
      busy.current = false
    }
  }

  // ---------------- cancel a gift / close the project
  const [cancelRow, setCancelRow] = useState<Gift | null>(null)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [actionError, setActionError] = useState('')
  const [closing, setClosing] = useState(false)
  const [endOpen, setEndOpen] = useState(false)
  const [endDate, setEndDate] = useState('')
  const [endError, setEndError] = useState('')
  const cancelM = useMutation({ mutationFn: (v: { id: string; reason: string }) => reversePayment(v.id, v.reason) })
  const closeM = useMutation({ mutationFn: () => closeProject(projectId) })
  const endM = useMutation({ mutationFn: (d: string | null) => setProjectEndDate(projectId, d) })
  const submitEnd = async (e: FormEvent) => {
    e.preventDefault()
    if (endM.isPending) return
    if (endDate !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return setEndError('Pick a date, or clear it for no end date')
    setEndError('')
    try {
      await endM.mutateAsync(endDate === '' ? null : endDate)
      await refresh()
      setEndOpen(false)
      toast.success(endDate === '' ? 'The project now has no end date.' : 'The end date was changed.')
    } catch (err) {
      setEndError(errText(err))
    }
  }

  const submitCancel = async (e: FormEvent) => {
    e.preventDefault()
    if (!cancelRow || cancelM.isPending) return
    const p = reasonSchema.safeParse({ reason })
    if (!p.success) return setReasonError(p.error.issues[0].message)
    setReasonError('')
    setActionError('')
    try {
      await cancelM.mutateAsync({ id: cancelRow.id, reason: p.data.reason })
      await refresh()
      setCancelRow(null)
      toast.success('The gift was cancelled. A matching negative entry was added.')
    } catch (err) {
      setActionError(errText(err))
      void refresh()
    }
  }
  const confirmClose = async () => {
    if (closeM.isPending) return
    setActionError('')
    try {
      await closeM.mutateAsync()
      await refresh()
      setClosing(false)
      toast.success('The project is closed. It takes no new gifts.')
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const project = projectQ.data
  const gifts = giftsQ.data ?? []
  const members = (membersQ.data ?? []).filter((m) => m.leftOn === null)
  const open_ = project !== null && project !== undefined && project.closedAt === null

  if (projectQ.isPending) return <SkeletonRows rows={4} />
  if (projectQ.isError) {
    return (
      <div>
        <p role="alert" className="text-body text-bad">{errText(projectQ.error)}</p>
        <Button variant="secondary" size="lg" className="mt-3" onClick={() => void projectQ.refetch()}>Try again</Button>
      </div>
    )
  }
  if (!project) {
    return <EmptyState title="Project not found" action={<Link to="/d/$deptId/projects" params={{ deptId }} className="text-brand hover:underline">Back to projects</Link>} />
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/d/$deptId/projects" params={{ deptId }} className="text-body text-brand hover:underline">Back to projects</Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h2 className="text-title font-semibold text-ink">{project.name}</h2>
          {project.departmentId === null && <Badge tone="info">Whole church</Badge>}
          {project.closedAt && <Badge tone="neutral">Closed</Badge>}
        </div>
        {project.description && <p className="mt-1 text-muted">{project.description}</p>}
        {(project.startsOn || project.endsOn) && (
          <p className="mt-1 text-body text-muted">
            {project.startsOn ? `From ${formatDay(project.startsOn)}` : ''}{project.startsOn && project.endsOn ? ' ' : ''}{project.endsOn ? `until ${formatDay(project.endsOn)}. Gifts dated after that are not accepted.` : ''}
          </p>
        )}
      </div>


      <Card>
        <CardBody>
          <p className="text-body text-muted">Raised by this department</p>
          <p className="text-title font-semibold text-ink">
            {formatKwacha(project.raisedNgwee)}
            {project.targetNgwee !== null && <span className="text-card font-normal text-muted"> of {formatKwacha(project.targetNgwee)}</span>}
          </p>
          {project.targetNgwee !== null && (
            <div className="mt-2 h-2 max-w-md overflow-hidden rounded-full bg-neutral-tint" role="img" aria-label={`${percent(project.raisedNgwee, project.targetNgwee)}% of the target`}>
              <div className="h-full bg-brand" style={{ width: `${percent(project.raisedNgwee, project.targetNgwee)}%` }} />
            </div>
          )}
          {project.departmentId === null && <p className="mt-2 text-micro text-muted">This is a whole-church project. You see only what your department gave.</p>}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Gifts</h3>
          {canWrite && open_ && <Button size="lg" onClick={openForm}>Record a gift</Button>}
        </CardHeader>
        <CardBody>
          {giftsQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : giftsQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(giftsQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void giftsQ.refetch()}>Try again</Button>
            </div>
          ) : gifts.length === 0 ? (
            <EmptyState title="No gifts yet" description={canWrite && open_ ? 'Record a gift when a member gives.' : 'Nothing has been given.'} />
          ) : (
            <ul className="divide-y divide-line">
              {gifts.map((g) => (
                <li key={g.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{g.memberName}</p>
                      <p className="text-body text-muted">{formatDay(g.receivedOn)} · {METHOD_LABELS[g.method]}{g.reference ? ` · ${g.reference}` : ''}</p>
                      {g.reversesId && <p className="text-micro text-muted">Correction: {g.reversalReason}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="font-medium text-ink">{formatKwacha(g.amountNgwee)}</span>
                      {g.status === 'reversed' && <Badge tone="danger">Cancelled</Badge>}
                    </div>
                  </div>
                  {!g.reversesId && g.amountNgwee > 0 && (
                    <OwnerDeleteButton table="contribution" id={g.id} what="gift" wrap="flex flex-wrap gap-2" />
                  )}
                  {canWrite && g.status === 'posted' && !g.reversesId && (
                    <button type="button" onClick={() => { setActionError(''); setReason(''); setReasonError(''); setCancelRow(g) }} className="inline-flex min-h-[44px] items-center rounded-control px-3 text-body font-medium text-bad hover:bg-neutral-tint">
                      Cancel this gift
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {canWrite && open_ && (project.departmentId !== null || profile.isAdmin) && (
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" size="lg" onClick={() => { setEndError(''); setEndDate(project.endsOn ?? ''); setEndOpen(true) }}>Change the end date</Button>
          <Button variant="secondary" size="lg" onClick={() => { setActionError(''); setClosing(true) }}>Close this project</Button>
        </div>
      )}
      <OwnerDeleteButton
        table="project"
        id={projectId}
        what="project"
        wrap="flex flex-wrap gap-3"
        onDeleted={() => void navigate({ to: '/d/$deptId/projects', params: { deptId } })}
      />

      <Sheet isOpen={open} onClose={() => setOpen(false)} title="Record a gift">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          <Select label="Who gave?" value={member} onChange={(e) => setMember(e.target.value)} options={[{ value: '', label: 'Choose a member' }, ...members.map((m) => ({ value: m.memberId, label: m.fullName }))]} error={errors.memberId} />
          <Input label="Amount in Kwacha" inputMode="decimal" placeholder="50.00" value={amount} onChange={(e) => setAmount(e.target.value)} error={errors.amount} />
          <Select label="How did they give?" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} options={(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABELS[m] }))} />
          <Input label="Date received" type="date" max={today} value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} error={errors.receivedOn} />
          <Input label="Reference (optional)" value={reference} onChange={(e) => setReference(e.target.value)} error={errors.reference} />
          {formError && <p role="alert" className="text-body text-bad">{formError}</p>}
          <Button type="submit" size="lg" isLoading={giftM.isPending}>{giftM.isPending ? 'Saving...' : 'Save gift'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={cancelRow !== null} onClose={() => setCancelRow(null)} title="Cancel this gift?">
        <form onSubmit={(e) => void submitCancel(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-neutral">{cancelRow ? `${cancelRow.memberName}, ${formatKwacha(cancelRow.amountNgwee)}. ` : ''}It stays on the record with a matching negative entry.</p>
          <Textarea label="Why?" value={reason} onChange={(e) => setReason(e.target.value)} error={reasonError} rows={3} />
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button type="submit" size="lg" variant="danger" isLoading={cancelM.isPending}>Cancel gift</Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => setCancelRow(null)}>Keep it</Button>
        </form>
      </Sheet>

      <Sheet isOpen={endOpen} onClose={() => setEndOpen(false)} title="Change the end date">
        <form onSubmit={(e) => void submitEnd(e)} className="flex flex-col gap-4" noValidate>
          <p className="text-neutral">Gifts dated after the end date are not accepted. Clear it for a project with no end.</p>
          <Input label="Ends on" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} error={endError} />
          <Button type="submit" size="lg" isLoading={endM.isPending}>Save</Button>
        </form>
      </Sheet>

      <Sheet isOpen={closing} onClose={() => setClosing(false)} title="Close this project?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">It will take no new gifts. Gifts already recorded stay, and can still be cancelled if one was a mistake.</p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button size="lg" isLoading={closeM.isPending} onClick={() => void confirmClose()}>Close project</Button>
          <Button variant="secondary" size="lg" onClick={() => setClosing(false)}>Keep it open</Button>
        </div>
      </Sheet>
    </div>
  )
}
